import { DomainStore } from "./domainStore";
import { observable, runInAction, action } from "mobx";
import {
  sendLogin,
  checkLogin,
  sendGoogleLogin,
  sendMfaLogin,
  sendMfaEmailCode,
  LoginError,
} from "./persistence/persistLogin";
import { toastError } from "../domain/errorHandling/toaster";
import Cookies from "js-cookie";
import { ITeamMember } from "encharge-domain/definitions/TeamMember";
import { segmentIdentify } from "domain/helpers/segment.io";
import { getQuery } from "domain/helpers/query";
import { redirectWithReload } from "domain/helpers/redirect";
import {
  confirmLoginErrorMessage,
  confirmEmailErrorMessage,
} from "encharge-domain/lib/helpers/login_helper";

/**
 * Which failures kill a challenge outright.
 *
 * 10091 - the challenge expired or a newer one superseded it.
 * 10092 - two-factor is no longer enabled on the account at all, e.g. an
 *         owner ran an admin reset, or the member turned it off in another
 *         tab, while this challenge was open.
 *
 * Neither can ever succeed, so the code screen is a dead end and the stashed
 * copy must go with it. A wrong code (10093) is NOT here: that challenge is
 * still good and the user is about to retype six digits.
 */
const isDeadChallenge = (e: unknown) => {
  const code = (e as LoginError)?.errorCode;
  return code === 10091 || code === 10092;
};

export class AuthStore {
  rootStore: DomainStore;
  constructor(rootStore: DomainStore) {
    this.rootStore = rootStore;
    this.loggedIn = true;
    // readonly, don't try to login
    if (
      this.rootStore.permissionsStore.readOnlyFlow ||
      window.location.pathname.includes("/view-form")
    )
      return;
    this.tryLogin();
  }

  @observable
  loggedIn?: boolean;

  @action
  async logOut() {
    Cookies.remove("connect.sid", { path: "/", domain: ".encharge.io" });
    // if domain is localhost, remove localhost cookie too
    if (window.location.hostname === "localhost") {
      Cookies.remove("connect.sid", { path: "/", domain: "localhost" });
    }
    this.loggedIn = false;
    // redirect to login
    redirectWithReload("/login");
  }

  @action
  async logIn(credentials: {
    email?: string;
    password?: string;
    googleToken?: string;
  }) {
    const uiStore = this.rootStore.uiStore;
    let teamMember: ITeamMember | undefined;
    try {
      if (credentials.email && credentials.password) {
        uiStore.loggingIn.loading = true;
        uiStore.loggingIn.loadingGoogleSignin = false;
        uiStore.loggingIn.showLogin = true;
        uiStore.loggingIn.error = undefined;

        teamMember = await sendLogin(credentials.email, credentials.password);
      } else if (credentials.googleToken) {
        uiStore.loggingIn.loadingGoogleSignin = true;
        uiStore.loggingIn.loading = false;
        uiStore.loggingIn.showLogin = true;
        uiStore.loggingIn.error = undefined;

        teamMember = await sendGoogleLogin(credentials.googleToken);
      } else {
        throw new Error("Missing email or password.");
      }
      if (teamMember) {
        await segmentIdentify({
          accountId: teamMember.accountId,
          teamMemberId: teamMember.id,
          traits: { email: teamMember.email },
        });
      }

      // All good, we are logged in. Save in state and go on our merrily way.
      runInAction(() => {
        uiStore.loggingIn.loading = false;
        uiStore.loggingIn.loadingGoogleSignin = false;
        uiStore.loggingIn.redirect = true;
        uiStore.loggingIn.showLogin = false;
        uiStore.loggingIn.error = undefined;

        this.loggedIn = true;
      });
    } catch (e) {
      // The password was right but a second factor is required - not an
      // error, the next step of the login. Switch to the code screen.
      if (
        (e as LoginError)?.errorCode === 10090 &&
        (e as LoginError)?.data?.mfaChallengeToken
      ) {
        runInAction(() => {
          uiStore.loggingIn.loading = false;
          uiStore.loggingIn.loadingGoogleSignin = false;
          uiStore.loggingIn.showLogin = true;
          uiStore.loggingIn.error = undefined;
          uiStore.loggingIn.mfaEmailSent = false;
          uiStore.loggingIn.mfaChallengeToken = (e as LoginError).data!.mfaChallengeToken;
        });
        return;
      }
      toastError({
        message: "We couldn't log you in.",
        extra: e,
      });

      runInAction(() => {
        uiStore.loggingIn.loading = false;
        uiStore.loggingIn.loadingGoogleSignin = false;
        uiStore.loggingIn.showLogin = true;
        const message = (e as any)?.response?.error?.message || e.message;
        if (message === confirmLoginErrorMessage) {
          uiStore.loggingIn.error = confirmLoginErrorMessage;
        } else if (message === confirmEmailErrorMessage) {
          // Unconfirmed signup email. login() has already (re)sent the
          // confirmation link, so just surface the instruction.
          uiStore.loggingIn.error = confirmEmailErrorMessage;
        }
      });
    }
  }

  /**
   * Second step of an MFA login. On success behaves exactly like a
   * successful logIn: state flips and the login page redirects into the app.
   */
  async completeMfaLogin({
    code,
    method,
    rememberDevice,
  }: {
    code: string;
    method?: "totp" | "recovery" | "email";
    rememberDevice?: boolean;
  }) {
    const uiStore = this.rootStore.uiStore;
    const challengeToken = uiStore.loggingIn.mfaChallengeToken;
    if (!challengeToken) {
      return;
    }
    try {
      runInAction(() => {
        uiStore.loggingIn.loading = true;
        uiStore.loggingIn.error = undefined;
      });
      const teamMember = await sendMfaLogin({
        challengeToken,
        code,
        method,
        rememberDevice,
      });
      if (teamMember) {
        await segmentIdentify({
          accountId: teamMember.accountId,
          teamMemberId: teamMember.id,
          traits: { email: teamMember.email },
        });
      }
      runInAction(() => {
        uiStore.loggingIn.loading = false;
        uiStore.loggingIn.mfaChallengeToken = undefined;
        uiStore.loggingIn.mfaEmailSent = false;
        uiStore.loggingIn.redirect = true;
        uiStore.loggingIn.showLogin = false;
        this.loggedIn = true;
      });
      // spent: single-use server-side, so nothing should hand it back
      this.forgetChallengeFromHistory();
    } catch (e) {
      const dead = isDeadChallenge(e);
      runInAction(() => {
        uiStore.loggingIn.loading = false;
        if (dead) {
          // this challenge can never succeed - back to the password screen
          uiStore.loggingIn.mfaChallengeToken = undefined;
        }
        uiStore.loggingIn.error =
          (e as Error).message || "That code didn't work. Please try again.";
      });
      if (dead) {
        // ...and a refresh must not undo that by re-seating the dead token
        this.forgetChallengeFromHistory();
      }
    }
  }

  /** "Email me a code" on the second-factor screen. */
  async requestMfaEmailCode() {
    const uiStore = this.rootStore.uiStore;
    const challengeToken = uiStore.loggingIn.mfaChallengeToken;
    if (!challengeToken) {
      return;
    }
    try {
      await sendMfaEmailCode(challengeToken);
      runInAction(() => {
        uiStore.loggingIn.mfaEmailSent = true;
        uiStore.loggingIn.error = undefined;
      });
    } catch (e) {
      // "Email me a code" can hit the same dead ends as submitting one, and
      // leaving the screen up under "please log in again" is a dead end.
      const dead = isDeadChallenge(e);
      runInAction(() => {
        if (dead) {
          uiStore.loggingIn.mfaChallengeToken = undefined;
          uiStore.loggingIn.mfaEmailSent = false;
        }
        uiStore.loggingIn.error = (e as Error).message;
      });
      if (dead) {
        this.forgetChallengeFromHistory();
      }
    }
  }

  /**
   * Back to the authenticator app from the recovery-code or emailed-code
   * view. The challenge itself is untouched - this only changes which kind
   * of code we expect next, so it is not one of the death paths.
   *
   * Without this, requesting an emailed code was a one-way door: if the mail
   * never arrived, the working authenticator app was unreachable and the only
   * exit was cancelling the challenge and retyping the password.
   */
  useAuthenticatorApp() {
    const uiStore = this.rootStore.uiStore;
    runInAction(() => {
      uiStore.loggingIn.mfaEmailSent = false;
      uiStore.loggingIn.error = undefined;
    });
  }

  /** Back to the password screen from the second-factor screen. */
  cancelMfaChallenge() {
    const uiStore = this.rootStore.uiStore;
    runInAction(() => {
      uiStore.loggingIn.mfaChallengeToken = undefined;
      uiStore.loggingIn.mfaEmailSent = false;
      uiStore.loggingIn.error = undefined;
    });
    // going back is a decision, not a pause - a refresh must not undo it
    this.forgetChallengeFromHistory();
  }

  /**
   * The magic-link, confirm-email and AppSumo doors all land on
   * /login?mfaChallenge=... - pick the challenge up from wherever it is.
   *
   * After the first adoption the query param is gone (see below), so on a
   * refresh the token comes back out of history.state instead.
   */
  adoptMfaChallengeFromLocation() {
    let token: string | undefined;
    try {
      token =
        (getQuery().mfaChallenge as string) ||
        (window.history?.state?.mfaChallenge as string);
    } catch (e) {
      return;
    }
    if (!token) {
      return;
    }
    this.adoptMfaChallengeFromUrl(String(token));
  }

  /** The magic-link redirect lands on /login?mfaChallenge=... - adopt it. */
  adoptMfaChallengeFromUrl(token: string) {
    const uiStore = this.rootStore.uiStore;
    runInAction(() => {
      uiStore.loggingIn.mfaChallengeToken = token;
      uiStore.loggingIn.showLogin = true;
      uiStore.loggingIn.loading = false;
    });
    // Once the token is in the store the URL copy is pure liability: it
    // outlives the login in browser history and in anything that records
    // addresses. The nonce makes it single-use and it expires in minutes,
    // but there is no reason to leave it lying in the address bar.
    this.scrubChallengeFromUrl(token);
  }

  /**
   * Drop the stashed challenge. Called wherever a challenge stops being
   * usable - cancelled, expired, or spent - because the whole point of
   * stashing it was that a refresh reads it back, and a refresh must not
   * resurrect one of those.
   *
   * Rebuilds the state without the key rather than nulling it, and keeps
   * everything else (react-router's key lives here too).
   */
  private forgetChallengeFromHistory() {
    try {
      if (!window?.history?.replaceState) {
        return;
      }
      const state = window.history.state;
      if (!state || !("mfaChallenge" in state)) {
        return;
      }
      const { mfaChallenge, ...rest } = state;
      window.history.replaceState(
        rest,
        "",
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      );
    } catch (e) {
      // never fail a login over history bookkeeping
    }
  }

  /**
   * Move the challenge out of the address bar and into history.state.
   *
   * That takes it out of the URL, and so out of the address bar, screenshots,
   * referrer headers and proxy logs, while leaving it somewhere a refresh can
   * read back. It does NOT make it vanish: history.state belongs to the
   * session history entry and browsers persist it to disk for session
   * restore. The honest claim is "not in the URL", not "not stored".
   */
  private scrubChallengeFromUrl(token: string) {
    try {
      if (!window?.history?.replaceState) {
        return;
      }
      const url = new URL(window.location.href);
      if (!url.searchParams.has("mfaChallenge")) {
        return;
      }
      url.searchParams.delete("mfaChallenge");
      window.history.replaceState(
        { ...window.history.state, mfaChallenge: token },
        "",
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch (e) {
      // a URL we cannot parse is not worth failing a login over
    }
  }

  async tryLogin() {
    try {
      await checkLogin();
      runInAction(() => {
        this.loggedIn = true;
      });
    } catch (error) {
      // unauthorized
      if (error.response && error.response.status === 401) {
        runInAction(() => {
          this.loggedIn = false;
        });
      } else {
        // TODO
        // EN-783 Check for internet connectivity and display retry overlay
        toastError(
          "We are having issues connecting to our servers. Please check your internet connection and reload."
        );
      }
    }
  }
}
